import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Archive,
  Briefcase,
  Building2,
  Calendar,
  Check,
  Clock,
  FileText,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Mic,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";

/* Solid brand palette (no gradients) */
export const INK = "#061A21";
export const INK_2 = "#0A2630";
export const BRAND = "#009DB9";
export const BRAND_DARK = "#007a90";
export const BRAND_LIGHT = "#22D3EE";
export const TINT = "#E3F3F7";
export const MIST = "#EAF0F2";

export const LOGO_SRC = "/IZ-Logo.png";

export const WRAP = "mx-auto w-full max-w-[84rem] px-5 sm:px-8";

/* Fades a block in when it scrolls into view. */
export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

/*
  Background patterns, all solid colours:
  - grid: "checked paper" lines
  - checker: a real checkerboard of squares
  - dots: small dot matrix
*/
export function Pattern({ type = "grid", dark = false, fade = false, size = 48 }) {
  const line = dark ? "rgba(34,211,238,0.11)" : "rgba(0,122,144,0.14)";
  const square = dark ? "rgba(34,211,238,0.055)" : "rgba(0,122,144,0.07)";
  const dot = dark ? "rgba(34,211,238,0.28)" : "rgba(0,122,144,0.30)";
  const styles = {
    grid: {
      backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
    },
    checker: {
      backgroundImage: `linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%), linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%)`,
      backgroundSize: `${size * 1.5}px ${size * 1.5}px`,
      backgroundPosition: `0 0, ${size * 0.75}px ${size * 0.75}px`,
    },
    dots: {
      backgroundImage: `radial-gradient(${dot} 1.4px, transparent 1.4px)`,
      backgroundSize: "24px 24px",
    },
  };
  const mask = fade
    ? "radial-gradient(ellipse 90% 80% at 50% 35%, #000 40%, transparent 100%)"
    : undefined;
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{ ...styles[type], WebkitMaskImage: mask, maskImage: mask }}
    />
  );
}

export function Eyebrow({ children, dark = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-bold tracking-[0.16em] uppercase rounded-md border ${
        dark
          ? "text-cyan-300 border-cyan-400/30 bg-cyan-400/10"
          : "text-[#007a90] border-[#009DB9]/30 bg-white"
      }`}
    >
      <span className="w-1.5 h-1.5 rounded-sm" style={{ background: dark ? BRAND_LIGHT : BRAND }} />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, text, dark = false, center = true }) {
  return (
    <div className={`${center ? "mx-auto text-center" : ""} max-w-3xl mb-12 sm:mb-16`}>
      <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
      <h2
        className={`mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[44px] lg:leading-[1.1] ${
          dark ? "text-white" : "text-[#061A21]"
        }`}
      >
        {title}
      </h2>
      {text && (
        <p className={`mt-4 text-[16px] sm:text-[17px] leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
          {text}
        </p>
      )}
    </div>
  );
}

export function ButtonLink({ to, href, children, variant = "primary", className = "" }) {
  const { pathname } = useLocation();
  const base =
    "inline-flex items-center justify-center gap-2 h-12 px-7 rounded-lg text-[15px] font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#009DB9]";
  const variants = {
    primary: "text-white bg-[#009DB9] hover:bg-[#007a90] shadow-sm hover:-translate-y-0.5",
    outline:
      "text-[#061A21] bg-white border border-slate-300 hover:border-[#009DB9] hover:text-[#007a90] hover:-translate-y-0.5",
    light: "text-[#061A21] bg-white hover:bg-cyan-50 hover:-translate-y-0.5",
    ghostDark: "text-white border border-white/30 hover:bg-white/10",
    ghostLight: "text-white border border-white/25 bg-white/5 hover:bg-white/10",
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  // "#section" links only scroll in place on the landing page ("/"). From any
  // other page (for example /pricing) they go to "/#section" instead.
  const target = to ?? (href?.startsWith("#") && pathname !== "/" ? `/${href}` : null);
  return target ? (
    <Link to={target} className={cls}>
      {children}
    </Link>
  ) : (
    <a
      href={href}
      className={cls}
      {...(href?.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

export function HashLink({ href, children, ...props }) {
  const { pathname } = useLocation();
  return pathname === "/" ? (
    <a href={href} {...props}>
      {children}
    </a>
  ) : (
    <Link to={`/${href}`} {...props}>
      {children}
    </Link>
  );
}

export function IconTile({ icon: Icon, dark = false, size = 22 }) {
  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl ${
        dark ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/25" : "text-white"
      }`}
      style={dark ? undefined : { background: BRAND }}
    >
      <Icon size={size} strokeWidth={2} />
    </div>
  );
}

export function Bullet({ children, dark = false }) {
  return (
    <li className={`flex items-start gap-3 text-[15px] ${dark ? "text-slate-200" : "text-slate-700"}`}>
      <span
        className={`flex items-center justify-center flex-shrink-0 w-5 h-5 mt-0.5 rounded-md ${
          dark ? "bg-cyan-400/15 text-cyan-300" : "bg-[#009DB9] text-white"
        }`}
      >
        <Check size={12} strokeWidth={3} />
      </span>
      <span>{children}</span>
    </li>
  );
}

export const ICONS = {
  Archive,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  FileText,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Mic,
  ShieldCheck,
  TrendingUp,
  Users,
};